import EmblaCarousel from "https://cdn.jsdelivr.net/npm/embla-carousel@8.6.0/+esm";
import EmblaCarouselAutoScroll from "https://cdn.jsdelivr.net/npm/embla-carousel-auto-scroll@8.6.0/+esm";
import { debounce } from "@theme/utilities";

class CustomerScroll extends HTMLElement {
  /** @type {number | null} */
  #currentWidth = null;
  #currentNumberOfCopies = 0;
  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  constructor() {
    super();
    this.viewportNode = this.querySelector(".customer-scroll__viewport");
    this.containerNode = this.querySelector(".customer-scroll__container");
    this.autoscroll = null;
    this.embla = null;
    this.reverseInterval = null;
  }

  /**
   * Get original slides (marked with data-original-slide attribute)
   * @returns {HTMLElement[]}
   */
  #getOriginalSlides() {
    if (!this.containerNode) return [];
    return Array.from(this.containerNode.children)
      .filter((slide) => slide.hasAttribute("data-original-slide"))
      .map((slide) => /** @type {HTMLElement} */ (slide));
  }

  /**
   * Calculate how many copies of slides are needed to fill the viewport
   * @returns {Promise<number>}
   */
  async #queryNumberOfCopies() {
    const originalSlides = this.#getOriginalSlides();
    if (originalSlides.length === 0) {
      return 1;
    }

    // Get viewport width (the customer-scroll element)
    const viewportWidth = this.clientWidth || 0;

    // Get total width of all original slides
    const slidesWidth = originalSlides.reduce((total, slide) => {
      return total + slide.getBoundingClientRect().width;
    }, 0);

    return slidesWidth === 0 ? 1 : Math.ceil(viewportWidth / slidesWidth) + 1; // +1 for extra buffer
  }

  /**
   * Mark original slides and remove any existing clones
   */
  #markOriginalSlides() {
    if (!this.containerNode) return;

    const allSlides = Array.from(this.containerNode.children);

    // Mark all current slides as original (in case this is first run)
    // or remove clones if they exist
    allSlides.forEach((slide) => {
      if (slide.hasAttribute("data-cloned-slide")) {
        slide.remove();
      } else {
        slide.setAttribute("data-original-slide", "true");
      }
    });
  }

  /**
   * Add cloned slides
   * @param {number} numberOfCopies - Number of times to duplicate all original slides
   */
  #addRepeatedSlides(numberOfCopies) {
    if (!this.containerNode) return;

    const originalSlides = this.#getOriginalSlides();
    if (originalSlides.length === 0) return;

    // Clone only the original slides (not any that were already cloned)
    for (let i = 0; i < numberOfCopies; i++) {
      originalSlides.forEach((slide) => {
        const clone = /** @type {HTMLElement} */ (slide.cloneNode(true));
        clone.setAttribute("aria-hidden", "true");
        clone.setAttribute("data-cloned-slide", "true");
        clone.removeAttribute("data-original-slide");
        if (this.containerNode) {
          this.containerNode.appendChild(clone);
        }
      });
    }
  }

  /**
   * Setup slides by duplicating them to ensure seamless looping
   */
  async setupSlides() {
    if (!this.containerNode) return;

    // Mark original slides and clean up any existing clones
    this.#markOriginalSlides();

    const originalSlides = this.#getOriginalSlides();
    if (originalSlides.length === 0) return;

    // Calculate how many copies we need
    const numberOfCopies = await this.#queryNumberOfCopies();

    // Add the required number of copies
    this.#addRepeatedSlides(numberOfCopies);
    this.#currentNumberOfCopies = numberOfCopies;
  }

  #handleResize = debounce(async () => {
    // If the width hasn't changed, don't recalculate the number of copies.
    if (this.#currentWidth && this.#currentWidth === this.clientWidth) return;
    this.#currentWidth = this.clientWidth;

    const newNumberOfCopies = await this.#queryNumberOfCopies();
    const currentNumberOfCopies = this.#currentNumberOfCopies;

    if (newNumberOfCopies > currentNumberOfCopies) {
      // Add more clones
      const difference = newNumberOfCopies - currentNumberOfCopies;
      this.#addRepeatedSlides(difference);
      this.#currentNumberOfCopies = newNumberOfCopies;

      // Reinitialize Embla to account for new slides
      if (this.embla) {
        this.embla.reInit();
      }
    }
  }, 250);

  async connectedCallback() {
    // Setup slides first (duplicate for seamless looping)
    await this.setupSlides();
    // Then initialize carousel
    this.initCarousel();
    // Add resize listener
    window.addEventListener("resize", this.#handleResize);
  }

  /**
   * Handle viewport intersection to play/stop autoscroll
   */
  #setupIntersectionObserver() {
    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReducedMotion || !this.embla) return;

    // Get the autoscroll plugin instance
    const autoscrollPlugin = this.embla.plugins().autoScroll;
    if (!autoscrollPlugin) return;

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && autoscrollPlugin) {
            autoscrollPlugin.play();
          } else if (autoscrollPlugin) {
            autoscrollPlugin.stop();
          }
        });
      },
      {
        threshold: 0.1,
      }
    );

    this.#intersectionObserver.observe(this);
  }

  async initCarousel() {
    if (!this.viewportNode || !this.containerNode) return;

    // Get speed factor from data attribute or use default
    const movementDirection = this.dataset.movementDirection || "normal";

    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    this.embla = EmblaCarousel(
      this.viewportNode,
      {
        loop: true,
        align: "start",
        dragFree: true,
        skipSnaps: false,
        containScroll: "keepSnaps",
      },
      prefersReducedMotion
        ? []
        : [
            EmblaCarouselAutoScroll({
              speed: 1,
              playOnInit: false,
              stopOnInteraction: true,
              direction:
                movementDirection === "reverse" ? "backward" : "forward",
            }),
          ]
    );
    this.#setupIntersectionObserver();
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.#handleResize);
    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }
    if (this.embla) {
      this.embla.destroy();
    }
  }
}

customElements.define("customer-scroll", CustomerScroll);
